import {cifValidator} from './cif.validator';
import {dniValidator} from './dni.validator';
import {luhnValidator} from './luhn.validator';
import {nieValidator} from './nie.validator';
import {REGISTRATION_NUMBER_EXAMPLES_BY_COUNTRIES} from './registrationNumbers.exemples';

/**
 * IMPORTANT: When updating rules in this file, make sure to also update the corresponding
 * backend validation rules in:
 * lib-platform/company-validation/src/main/kotlin/com/malt/validation/company/CountryCode.kt
 *
 * Both files must be kept in sync to ensure consistent registration number validation across backend and frontend.
 */
const ANDORRA_REGISTRATION_REGEX = /^[A-Z]{1}-[0-9]{6}-[A-Z]{1}$/;
const ARGENTINA_REGISTRATION_REGEX = /^[0-9]{2}-?[0-9]{8}-?[0-9]$/;
const AUSTRALIA_REGISTRATION_REGEX = /(^[0-9]{8}$)|(^[0-9]{2} ?[0-9]{3} ?[0-9]{3} ?[0-9]{3}$)|(^[0-9]{3} ?[0-9]{3} ?[0-9]{3}$)/;
const AUSTRIAN_REGISTRATION_REGEX = /(^[A-Z]\d{8}$)|(^(FN)?[1-9][0-9]{4,5}[A-za-z]$)|(^[0-9]{2}[- ]?[0-9]{3}\/?[0-9]{4}$)|(^[0-9]{8}$)/;
const BELARUS_REGISTRATION_REGEX = /^[0-9A-Za-z]{9}$/;
const BELGIUM_REGISTRATION_REGEX = /(^[0|1|2]\d{3}\.?\d{3}\.?\d{3}$)|(^[0-9]{11}$)/;
const BRAZIL_REGISTRATION_REGEX = /^[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}-?[0-9]{2}|[0-9]{2}\.?[0-9]{3}\.?[0-9]{3}\/?[0-9]{4}-?[0-9]{2}$/;
const BULGARIA_REGISTRATION_REGEX = /^[0-9]{9,10}$/;
const CANADA_REGISTRATION_REGEX = /(^[0-9]{2}-[0-9]{3}-[0-9]{4}$)|(^[0-9]{9}(R[TP][0-9]{4})?$)|(^[0-9]{10}$)$/;
const COLOMBIA_REGISTRATION_REGEX =
    /(^[0-9]{1}\.?[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}$)|(^[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}-?[0-9]{1}$)|(^[0-9]{2}\.?[0-9]{3}\.?[0-9]{3}$)|(^[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}$)/;
const CHILE_REGISTRATION_REGEX =
    /(^[0-9]{2}\.[0-9]{3}\.[0-9]{3}-[0-9]{1}$)|(^[0-9]{2}[0-9]{3}[0-9]{3}-[0-9]{1}$)|(^[0-9]{2}\.[0-9]{3}\.[0-9]{3}-K$)|(^[0-9]{2}[0-9]{3}[0-9]{3}-K$)/;
const CROATIA_REGISTRATION_REGEX = /^(HR)?[0-9]{11}$/;
const CYPRUS_REGISTRATION_REGEX = /(^(CY)?-?[0-9A-Za-z]{8}[A-Za-z]$)|(^HE[0-9]{6}$)/;
const CZECH_REGISTRATION_REGEX = /^((CZ)?[0-9]{6}\/?[0-9]{3,4})|((CZ)?[0-9]{8,10})$/;
const DANISH_REGISTRATION_REGEX = /(^[0-9]{6}-?([0-9]{0,4})$)|(^[0-9]{8}$)/;
const EGYPT_REGISTRATION_REGEX = /^[0-9]{3}-?[0-9]{3}-?[0-9]{3}$/;
const ESTONIA_REGISTRATION_REGEX = /(^[0-9]{11}$)|(^[0-9]{8}$)/;
const FINNISH_REGISTRATION_REGEX = /(^[0-9]{6}[-+A][0-9]{3}[A-Z]$)|(^[0-9]{7}-[0-9]$)|(^[0-9]{6}[-+A][0-9]{3}[L9]$)/;
const FRENCH_POLYNESIA_REGISTRATION_REGEX = /(^[a-zA-Z]?\d{5,6}$)|(^[0-9]{14}$)/;
const GEORGIA_REGISTRATION_REGEX = /^([0-9]{9})|([0-9]{11})$/;
const GERMAN_REGISTRATION_REGEX = new RegExp(
    [
        '^(DE)[0-9]{9}$', // German VAT number
        'HRB?\\s?\\d{1,6}[A-Z]{0,2}$', // Commercial Register B
        '^(HRA?|GNR?)\\d{1,6}[A-Z]{0,2}$', // Commercial Register A + Cooperative Register
        '^PR\\s?\\d{1,6}[A-Z]{0,2}$', // Partnership Register (Partnerschaftsregister)
        '^[0-9]{11}$', // Tax ID (11 digits)
        '^VR([0-9]{2})?[0-9]{4}$', // Association Register
        // Tax number formats (unified - without slash)
        '^28\\d{2}0\\d{3}\\d{4}\\d$', // Baden-Württemberg
        '^9\\d{3}0\\d{3}\\d{4}\\d$', // Bavaria
        '^11\\d{2}0\\d{3}\\d{4}\\d$', // Berlin
        '^30\\d{2}0\\d{3}\\d{4}\\d$', // Brandenburg
        '^24\\d{2}0\\d{3}\\d{4}\\d$', // Bremen
        '^\\d{2}\\d{3}\\d{4}\\d$', // Bremen (second format)
        '^22\\d{2}0\\d{3}\\d{4}\\d$', // Hamburg
        '^26\\d{2}0\\d{3}\\d{4}\\d$', // Hesse
        '^0\\d{2}\\d{3}\\d{4}\\d$', // Hesse (second format)
        '^40\\d{2}0\\d{3}\\d{4}\\d$', // Mecklenburg-Western Pomerania
        '^23\\d{2}0\\d{3}\\d{4}\\d$', // Lower Saxony
        '^5\\d{3}0\\d{4}\\d{3}\\d$', // North Rhine-Westphalia
        '^5\\d{3}/\\d{4}/\\d{3}\\d$', // North Rhine-Westphalia (second format)
        '^27\\d{2}0\\d{3}\\d{4}\\d$', // Rhineland-Palatinate
        '^10\\d{2}0\\d{3}\\d{4}\\d$', // Saarland
        '^(32)?\\d{2}0\\d{3}\\d{4}\\d$', // Saxony (with optional 32 prefix)
        '^31\\d{2}0\\d{3}\\d{4}\\d$', // Saxony-Anhalt
        '^21\\d{2}0\\d{3}\\d{4}\\d$', // Schleswig-Holstein
        '^41\\d{2}0\\d{3}\\d{4}\\d$', // Thuringia
        // Tax number formats (with slash)
        '^\\d{2}\\d{3}/\\d{4}\\d$', // Baden-Württemberg, Hesse
        '^\\d{3}/\\d{3}/\\d{4}\\d$', // Bavaria
        '^\\d{2}/\\d{3}/\\d{4}\\d$', // Berlin, Hamburg, Lower Saxony, Rhineland-Palatinate, Schleswig-Holstein
        '^0\\d{2}/\\d{3}/\\d{4}\\d$', // Brandenburg, Mecklenburg-Western Pomerania, Saarland
        '^\\d{3}/\\d{4}/\\d{3}\\d$', // North Rhine-Westphalia
        '^2\\d{2}/\\d{3}/\\d{4}\\d$', // Saxony
        '^1\\d{2}/\\d{3}/\\d{4}\\d$', // Saxony-Anhalt, Thuringia
    ].join('|'),
);
const GREAT_BRITAIN_REGISTRATION_REGEX =
    /(^[A-Z]{2}[0-9]{6}[A-Z]?$)|(^[0-9]{10}$)|(^[0-9]{2}[A-Z][0-9]{5}$)|(^([01])([0-9]{7})$)|(^(SC)([0-9]{6})$)|(^(OC)([0-9]{6})$)|(^(LP)([0-9]{6})$)|(^(SO)([0-9]{6})$)|(^(SL)([0-9]{6})$)|(^(NI)([0-9]{6})$)|(^(FC)([0-9]{6})$)|(^(NC)([0-9]{6})$)|(^(NL)([0-9]{6})$)|(^(IP)([0-9]{5})([A-Z]|[0-9])$)|(^(R)([0-9]{7})$)/;
const GREECE_REGISTRATION_REGEX = /^[0-9]{9}$/;
const GUERNSEY_REGISTRATION_REGEX = /^CMP[0-9]{5}$/;
const HUNGARY_REGISTRATION_REGEX = /(^[0-9A-Za-z]{7,8}$)|(^[0-9A-Za-z]{10,11}$)/;
const ICELAND_REGISTRATION_REGEX = /(^[A-Z0-9]{10}$)|(^(IS)?[A-Z0-9]{5,6}$)/;
const ITALY_REGISTRATION_REGEX = /(^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$)|(^[0-9]{11}$)|(^[A-Z]{2}-[0-9]{6,7}$)/;
const INDIA_REGISTRATION_REGEX =
    /(^[A-Z]{5}[0-9]{4}[A-Z]$)|(^([LU])[0-9]{5}[A-Z]{2}[0-9]{4}((FLC)|(FTC)|(GAP)|(GAT)|(GOI)|(NPL)|(OPC)|(PLC)|(PTC)|(SGC)|(ULL)|(ULT))[0-9]{6}$)/;
const IRELAND_REGISTRATION_REGEX = /^[0-9]{5,7}[A-Z]?[A-Z]?$/;
const ISLE_OF_MAN_REGISTRATION_REGEX = /^[0-9]{6}[C|F|L]$/;
const KUWAIT_REGISTRATION_REGEX = /^[A-Z0-9]{12}$/;
const LATVIA_REGISTRATION_REGEX = /(^32[0-9]{9}$)|(^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])(\d{2})-?[0-9]{5}$)|(^[0-9]{11}$)/;
const LIECHSTENSTEIN_REGISTRATION_REGEX = /^[A-Z0-9]{4,12}$/;
const LITHUANIA_REGISTRATION_REGEX = /^[A-Z0-9]{9,12}$/;
const LUXEMBOURG_REGISTRATION_REGEX = /(^[A-Z][0-9]{1,6}$)|(^[A-Z0-9]{11,13}$)/;
const MALTA_REGISTRATION_REGEX = /(^[0-9]{9}$)|(^[0-9]{3,7}[MGAPLHBZ]|[A-Z][0-9]{5}$)/;
const MONACO_REGISTRATION_REGEX = /^\d{2}[SP]\d{5}$/;
const MOLDOVA_REGISTRATION_REGEX = /^\d{13}$/;
const MOROCCO_REGISTRATION_REGEX = /^[A-Z0-9]{7,20}$/;
const NETHERLANDS_REGISTRATION_REGEX = /^\d{8,9}$/;
const NEW_CALEDONIA_REGISTRATION_REGEX = /^\d{7}$/;
const NEW_ZEALAND_REGISTRATION_REGEX = /^(?:[0-9]{2}-?[0-9]{3}-?[0-9]{3}|[0-9]{3}-?[0-9]{3}-?[0-9]{3}|[0-9]{13})$/;
const NORWAY_REGISTRATION_REGEX = /(^[0-9]{11}$)|(^[0-9]{9}(MVA)?$)/;
const OMAN_REGISTRATION_REGEX = /^[A-Z0-9]{7,12}$/;
const POLAND_REGISTRATION_REGEX = /(^[0-9]{9}$)|(^[0-9]{10}$)|(^[0-9]{11}$)|(^[0-9]{14}$)/;
const PORTUGAL_REGISTRATION_REGEX = /^(PT)?[0-9]{9}$/;
const QATAR_REGISTRATION_REGEX = /(^[0-9]{10}$)|(^0[0-9]{4}$)/;
const ROMANIA_REGISTRATION_REGEX = /(^[0-9]{13}$)|(^(RO)?[0-9]{2,10}$)/;
const SAUDI_ARABIA_REGISTRATION_REGEX = /(^[0-9]{10}$)|(^[0-9]{15}$)/;
const SINGAPORE_REGISTRATION_REGEX =
    /(^[A-Z]{1}[0-9]{7}[A-Z]$)|(^[0-9]{8}[A-Z]{1}$)|(^[0-9]{9}[A-Z]{1}$)|(^F000[0-9]{5}[A-Z]{1}$)|(^F [0-9]{5}[A-Z]{1}$)|(^S[0-9]{2}LL[0-9]{4}[A-Z]$)|(^T[0-9]{2}LL[0-9]{4}[A-Z]$)/;
const SLOVAKIA_REGISTRATION_REGEX = /^([0-9A-Z]{9,10})|([0-9]{6})|([0-9]{8})$/;
const SLOVENIA_REGISTRATION_REGEX = /(^[0-9]{8}$)|(^[0-9]{10}$)/;
const SOUTH_AFRICA_REGISTRATION_REGEX = /^([0-9]{10})|((19|20)\d{2}\/\d{6}\/\d{2})$/;
const SWEDISH_REGISTRATION_REGEX = /(^[0-9]{6}\\+?[0-9]{4}$)|(^[0-9]{6}-?[0-9]{4}$)/;
const SWITZERLAND_REGISTRATION_REGEX =
    /(^(CHE)-[0-9]{3}\.[0-9]{3}\.[0-9]{3}$)|(^(CH)-[0-9]{3}\.[0-9]\.[0-9]{3}\.[0-9]{3}-[0-9]$)|(^[0-9]{3}\.[0-9]{4}\.[0-9]{4}\.[0-9]{2}$)/;
const THAILAND_REGISTRATION_REGEX = /^[0-9]{13}$/;
const TUNISIA_REGISTRATION_REGEX = /^[0-9]{7}[A-Z]$/;
const TURKEY_REGISTRATION_REGEX = /^[0-9]{10,11}$/;
const UKRAINE_REGISTRATION_REGEX = /^[0-9]{8,12}$/;
const UNITED_ARAB_EMIRATES_REGISTRATION_REGEX = /(^[0-9]{5,8}$)|(^[0-9A-z]{15}$)|(^CL[0-9]{4}$)|(^CN-[0-9]{7}$)|(^[0-9]{4}$)|(^[0-9]{12}$)/;
const USA_REGISTRATION_REGEX = /(^[0-9]{2}-[0-9]{3}-[0-9]{4}$)|(^[0-9]{9}$)|(^[0-9]{2}-[0-9]{7}$)/;
const FRANCE_RNA_REGISTRATION_REGEX = /^W[0-9]{9}$/;
const LAPOSTE_SIRET_PREFIX = '356000000';

const REGISTRATION_VALIDATORS_BY_COUNTRY = {
    AD: (val) => ANDORRA_REGISTRATION_REGEX.test(val),
    AE: (val) => UNITED_ARAB_EMIRATES_REGISTRATION_REGEX.test(val),
    AR: (val) => ARGENTINA_REGISTRATION_REGEX.test(val),
    AT: (val) => AUSTRIAN_REGISTRATION_REGEX.test(val),
    AU: (val) => AUSTRALIA_REGISTRATION_REGEX.test(val),
    BE: (val) => BELGIUM_REGISTRATION_REGEX.test(val),
    BG: (val) => BULGARIA_REGISTRATION_REGEX.test(val),
    BL: checkFrRegistrationNumber,
    BR: (val) => BRAZIL_REGISTRATION_REGEX.test(val),
    BY: (val) => BELARUS_REGISTRATION_REGEX.test(val),
    CA: (val) => CANADA_REGISTRATION_REGEX.test(val),
    CH: (val) => SWITZERLAND_REGISTRATION_REGEX.test(val),
    CL: (val) => CHILE_REGISTRATION_REGEX.test(val),
    CO: (val) => COLOMBIA_REGISTRATION_REGEX.test(val),
    CY: (val) => CYPRUS_REGISTRATION_REGEX.test(val),
    CZ: (val) => CZECH_REGISTRATION_REGEX.test(val),
    DE: (val) => GERMAN_REGISTRATION_REGEX.test(val),
    DK: (val) => DANISH_REGISTRATION_REGEX.test(val),
    EE: (val) => ESTONIA_REGISTRATION_REGEX.test(val),
    EG: (val) => EGYPT_REGISTRATION_REGEX.test(val),
    ES: checkEsRegistrationNumber,
    FI: (val) => FINNISH_REGISTRATION_REGEX.test(val),
    FR: checkFrRegistrationNumber,
    GB: (val) => GREAT_BRITAIN_REGISTRATION_REGEX.test(val),
    GE: (val) => GEORGIA_REGISTRATION_REGEX.test(val),
    GF: checkFrRegistrationNumber,
    GG: (val) => GUERNSEY_REGISTRATION_REGEX.test(val),
    GP: checkFrRegistrationNumber,
    GR: (val) => GREECE_REGISTRATION_REGEX.test(val),
    HR: (val) => CROATIA_REGISTRATION_REGEX.test(val),
    HU: (val) => HUNGARY_REGISTRATION_REGEX.test(val),
    IE: (val) => IRELAND_REGISTRATION_REGEX.test(val),
    IM: (val) => ISLE_OF_MAN_REGISTRATION_REGEX.test(val),
    IN: (val) => INDIA_REGISTRATION_REGEX.test(val),
    IS: (val) => ICELAND_REGISTRATION_REGEX.test(val),
    IT: (val) => ITALY_REGISTRATION_REGEX.test(val),
    KW: (val) => KUWAIT_REGISTRATION_REGEX.test(val),
    LI: (val) => LIECHSTENSTEIN_REGISTRATION_REGEX.test(val),
    LT: (val) => LITHUANIA_REGISTRATION_REGEX.test(val),
    LU: (val) => LUXEMBOURG_REGISTRATION_REGEX.test(val),
    LV: (val) => LATVIA_REGISTRATION_REGEX.test(val),
    MA: (val) => MOROCCO_REGISTRATION_REGEX.test(val),
    MC: (val) => MONACO_REGISTRATION_REGEX.test(val),
    MD: (val) => MOLDOVA_REGISTRATION_REGEX.test(val),
    MF: checkFrRegistrationNumber,
    MQ: checkFrRegistrationNumber,
    MT: (val) => MALTA_REGISTRATION_REGEX.test(val),
    NC: (val) => NEW_CALEDONIA_REGISTRATION_REGEX.test(val),
    NL: (val) => NETHERLANDS_REGISTRATION_REGEX.test(val),
    NO: (val) => NORWAY_REGISTRATION_REGEX.test(val),
    NZ: (val) => NEW_ZEALAND_REGISTRATION_REGEX.test(val),
    OM: (val) => OMAN_REGISTRATION_REGEX.test(val),
    PF: (val) => FRENCH_POLYNESIA_REGISTRATION_REGEX.test(val),
    PL: (val) => POLAND_REGISTRATION_REGEX.test(val),
    PM: checkFrRegistrationNumber,
    PT: (val) => PORTUGAL_REGISTRATION_REGEX.test(val),
    QA: (val) => QATAR_REGISTRATION_REGEX.test(val),
    RE: checkFrRegistrationNumber,
    RO: (val) => ROMANIA_REGISTRATION_REGEX.test(val),
    SA: (val) => SAUDI_ARABIA_REGISTRATION_REGEX.test(val),
    SE: (val) => SWEDISH_REGISTRATION_REGEX.test(val),
    SG: (val) => SINGAPORE_REGISTRATION_REGEX.test(val),
    SI: (val) => SLOVENIA_REGISTRATION_REGEX.test(val),
    SK: (val) => SLOVAKIA_REGISTRATION_REGEX.test(val),
    TF: checkFrRegistrationNumber,
    TH: (val) => THAILAND_REGISTRATION_REGEX.test(val),
    TN: (val) => TUNISIA_REGISTRATION_REGEX.test(val),
    TR: (val) => TURKEY_REGISTRATION_REGEX.test(val),
    UA: (val) => UKRAINE_REGISTRATION_REGEX.test(val),
    US: (val) => USA_REGISTRATION_REGEX.test(val),
    WF: checkFrRegistrationNumber,
    YT: checkFrRegistrationNumber,
    ZA: (val) => SOUTH_AFRICA_REGISTRATION_REGEX.test(val),
} satisfies Record<string, (val: string) => boolean>;

/**
 * Pure validation function returning a boolean

 * Check if a registration number is valid for the country provided
 *
 * @param value the registration number you want to check
 * @param countryCode the country you want to check
 * */
export function checkRegistrationByCountry(value: string, countryCode?: string): boolean {
    if (countryCode && countryCode in REGISTRATION_VALIDATORS_BY_COUNTRY) {
        const countryValidator = REGISTRATION_VALIDATORS_BY_COUNTRY[countryCode as keyof typeof REGISTRATION_VALIDATORS_BY_COUNTRY];
        return countryValidator(value);
    }
    return true;
}

/** Spain registration number validator */
function checkEsRegistrationNumber(value: string): boolean {
    return cifValidator(value) || dniValidator(value) || nieValidator(value);
}

/** France registration number validator */
function checkFrRegistrationNumber(value: string): boolean {
    // RNA (Répertoire national des associations): W + 9 digits
    if (FRANCE_RNA_REGISTRATION_REGEX.test(value)) {
        return true;
    }

    const areAllCharsNumbers = /^\d+$/.test(value);
    if (value.length !== 14 || !areAllCharsNumbers) {
        return false;
    }
    // Malt sirets
    if ((['79135487100067', '79135487100042', '79135487100018', '791354871'] as const).includes(value as any)) {
        return false;
    }
    // There's an exception for LaPoste company: all its sirets (except for head office..) have a specific format:
    // to be valid, the sum of digits must be a multiple of 5
    // https://www.sirene.fr/static-resources/doc/lettres/lettre-16-novembre-2013.pdf
    // https://fr.wikipedia.org/wiki/Syst%C3%A8me_d%27identification_du_r%C3%A9pertoire_des_%C3%A9tablissements
    if (value.startsWith(LAPOSTE_SIRET_PREFIX) && value !== LAPOSTE_SIRET_PREFIX + '00048') {
        const sumOfDigits = value
            .split('')
            .map((n) => parseInt(n))
            .reduce((acc, value) => (acc += value));
        return sumOfDigits % 5 === 0;
    }

    return luhnValidator(value);
}

export function getRegistrationNumberExampleByCountry(countryCode?: string): string | null {
    return countryCode
        ? (REGISTRATION_NUMBER_EXAMPLES_BY_COUNTRIES[countryCode as keyof typeof REGISTRATION_NUMBER_EXAMPLES_BY_COUNTRIES]?.example ?? null)
        : null;
}

export function getRegistrationNumberTypeByCountry(countryCode?: string): string | null {
    return countryCode
        ? (REGISTRATION_NUMBER_EXAMPLES_BY_COUNTRIES[countryCode as keyof typeof REGISTRATION_NUMBER_EXAMPLES_BY_COUNTRIES]?.type ?? null)
        : null;
}
